<script setup lang="ts">
/**
 * SceneSleep · 隐身小剧场：夜窗下盖被子睡觉
 * ------------------------------------------------------------
 * 舞台 = 一扇深夜的窗（星星 + 月亮，框条同步变暗）：小人枕着白枕头、
 * 盖着蓝被子躺在窗台小床上，被子随呼吸起伏，头顶冒 Zzz。
 * 人物头径与所有场景一致（r5.9 × 0.9 缩放 ≈ 10.6px，见 scenes/index.ts）。
 */
defineProps<{ peek?: boolean; hot?: boolean }>()
</script>

<template>
  <span class="sc" aria-hidden="true">
    <span class="win">
      <i class="moon" />
      <svg class="bed" viewBox="0 0 34 26">
        <defs>
          <linearGradient id="apShirtQuilt" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stop-color="#7A95FF" />
            <stop offset="1" stop-color="#4C6BF0" />
          </linearGradient>
        </defs>
        <!-- 床腿 + 床垫 + 枕头 -->
        <rect x="2.2" y="21.6" width="1.4" height="3" fill="#C9A87C" />
        <rect x="29.4" y="21.6" width="1.4" height="3" fill="#C9A87C" />
        <rect x=".7" y="18.6" width="32.6" height="3.2" rx="1.5" fill="#EADCC6" stroke="#D9C6A5" stroke-width=".7" />
        <rect x="1.7" y="15.9" width="7.4" height="2.9" rx="1.45" fill="#FFFFFF" stroke="#DFE6F0" stroke-width=".7" />
        <!-- 睡着的头（枕在枕头上，眼睛闭成两条缝） -->
        <circle cx="7" cy="13.4" r="5.9" fill="#FFDCB4" stroke="#E6B183" stroke-width=".9" />
        <path d="M1.3 12.9a5.9 5.9 0 0 1 11.8 0v.7H1.3z" fill="#3A2B21" />
        <path d="M6.7 7.3c.9-1.4 2.3-1.7 3-1.3-1.1.4-1.8.9-2.2 1.6z" fill="#3A2B21" />
        <path d="M4.2 14.1q.7.8 1.5 0M7.4 14.1q.7.8 1.5 0" stroke="#2B2118" stroke-width=".6"
              fill="none" stroke-linecap="round" />
        <path d="M5.9 16.1q.7.6 1.4 0" stroke="#C05A5A" stroke-width=".55" fill="none" stroke-linecap="round" />
        <ellipse cx="2.9" cy="15.5" rx="1" ry=".65" fill="#FF9EB5" opacity=".5" />
        <ellipse cx="10.5" cy="15.5" rx="1" ry=".65" fill="#FF9EB5" opacity=".5" />
        <!-- 被子（盖住全身，随呼吸起伏） -->
        <g class="quilt">
          <path d="M12.3 21.8v-3.6q0-3 3.4-3h13.4q3.4 0 3.4 3.2v3.4z" fill="url(#apShirtQuilt)" stroke="#FFFFFF66" stroke-width=".6" />
          <path d="M13.6 18.6q9.2 1 18.2.2" stroke="#FFFFFF88" stroke-width=".45" stroke-dasharray="1.1 1.1" fill="none" />
          <path d="M13.2 20.2q9.4.9 18.6.2" stroke="#FFFFFF88" stroke-width=".45" stroke-dasharray="1.1 1.1" fill="none" />
        </g>
        <!-- Zzz -->
        <text x="14.2" y="10" font-size="3.6" font-weight="600" fill="#EDF3FF" opacity="0">Z
          <animate attributeName="opacity" values="0;.95;0" dur="3.3s" begin="0s" repeatCount="indefinite" />
        </text>
        <text x="17.4" y="7.2" font-size="2.9" font-weight="600" fill="#EDF3FF" opacity="0">z
          <animate attributeName="opacity" values="0;.85;0" dur="3.3s" begin="1.1s" repeatCount="indefinite" />
        </text>
        <text x="20.2" y="5" font-size="2.4" font-weight="600" fill="#EDF3FF" opacity="0">z
          <animate attributeName="opacity" values="0;.75;0" dur="3.3s" begin="2.2s" repeatCount="indefinite" />
        </text>
      </svg>
      <span class="mull" />
    </span>
  </span>
</template>

<style scoped>
.sc { position: absolute; inset: 0; display: block; font-size: 0; }

/* 深夜的窗：深蓝夜空 + 星星，框条同步变暗 */
.win {
  position: absolute; left: 50%; bottom: 7px;
  width: 36px; height: 26px; margin-left: -18px;
  border: 2px solid #44557A; border-radius: 8px;
  background:
    radial-gradient(circle at 76% 24%, #fffdf0 0 1.8px, transparent 2.2px),
    radial-gradient(circle at 28% 34%, #ffffff88 0 1px, transparent 1.5px),
    radial-gradient(circle at 55% 12%, #ffffff66 0 .9px, transparent 1.3px),
    linear-gradient(180deg, #1C2A4C 0%, #2C3D66 62%, #3A4C77 100%);
  box-shadow: inset 0 -2px 3px #00000030, 0 1px 2px #0b122012;
}
.mull {
  position: absolute; z-index: 3;
  left: 50%; top: 0; width: 2px; height: 100%; margin-left: -1px;
  background: #44557A;
}
/* 月亮画在舞台高位；坞站整格上移 9px 后仍落在窗框上沿，不冒出顶栏按钮 */
.moon {
  position: absolute; z-index: 1; right: 4px; top: 13px;
  width: 6.5px; height: 6.5px; border-radius: 50%;
  background: #FFF2C4; box-shadow: 0 0 7px 1.5px #FFEBB066;
}
/* 0.9px/viewBox 单位：34×26 → 30.6×23.4；头 r5.9 → 头径 ≈10.6px 与其他场景一致 */
.bed {
  position: absolute; left: 50%; bottom: 0; z-index: 2;
  width: 30.6px; height: 23.4px; margin-left: -15.3px;
  filter: drop-shadow(0 1px 1px #00000040);
}
/* 被子随呼吸轻轻起伏 */
.quilt {
  transform-box: fill-box; transform-origin: 50% 100%;
  animation: apBreathe 3.4s ease-in-out infinite;
}
@keyframes apBreathe {
  0%, 100% { transform: scaleY(1); }
  50% { transform: scaleY(1.06); }
}
</style>
